import { Navigate, useLocation } from "react-router-dom";
import { ForbiddenState } from "@/components/ForbiddenState";
import { useAdminSignOut } from "@/hooks/useAdminSignOut";
import { canAccessAdminPath, firstAccessibleAdminPath } from "@/lib/adminPermissions";
import { useAuthStore } from "@/stores/authStore";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route wrapper that redirects to login if not authenticated.
 * @param root0 - Component props
 * @param root0.children - Child components to render when authenticated
 * @returns React element - children, loading state, or redirect
 */
export function ProtectedRoute({ children }: ProtectedRouteProps): React.ReactElement {
  const { admin, isAuthenticated, isLoading, clearAuth } = useAuthStore();
  const location = useLocation();
  const handleSignOut = useAdminSignOut(clearAuth);

  if (isLoading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="border-hairline border-t-primary-600 mx-auto h-8 w-8 animate-spin rounded-full border-4"></div>
          <p className="text-foreground-muted mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const requestedPath = `${location.pathname}${location.search}${location.hash}`;
  if (!canAccessAdminPath(admin, requestedPath)) {
    return (
      <ForbiddenState destination={firstAccessibleAdminPath(admin)} onSignOut={handleSignOut} />
    );
  }

  return <>{children}</>;
}
